import * as React from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ExternalLink,
  MoveHorizontal,
  Timer,
} from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { StarField } from "@/components/StarField";
import { useInView } from "@/hooks/useInView";

const Slide = ({ project, isActive, offset, upcoming }) => {
  const Wrapper = upcoming ? "div" : "a";
  const wrapperProps = upcoming
    ? {}
    : {
        href: project.url,
        target: "_blank",
        rel: "noreferrer noopener",
      };

  return (
    <Wrapper
      {...wrapperProps}
      className={`group block select-none ${upcoming ? "" : "cursor-pointer"}`}
      style={{
        transform: `scale(${isActive ? 1 : 0.88}) rotate(${
          isActive ? 0 : offset > 0 ? 1.4 : -1.4
        }deg) translateY(${isActive ? 0 : 18}px)`,
        opacity: isActive ? 1 : 0.45,
        transition:
          "transform 700ms cubic-bezier(0.16,1,0.3,1), opacity 700ms ease-out",
      }}
    >
      <div
        className={`relative overflow-hidden border bg-[hsl(230_24%_7%)] transition-[border-color,box-shadow] duration-700 ${
          isActive
            ? upcoming
              ? "border-accent/45 shadow-[0_50px_100px_-50px_hsl(265_90%_60%/0.5)]"
              : "border-primary/45 shadow-[0_50px_100px_-50px_hsl(222_100%_55%/0.55)]"
            : "border-border/70"
        }`}
      >
        {/* Fensterleiste */}
        <div className="flex items-center gap-2 border-b border-border/70 bg-[hsl(230_22%_9%)] px-4 py-3">
          <span className="flex gap-1.5">
            {[0.25, 0.18, 0.12].map((alpha) => (
              <span
                key={alpha}
                className="h-2 w-2 rounded-full"
                style={{ background: `hsl(0 0% 100% / ${alpha})` }}
              />
            ))}
          </span>
          <span className="ml-2 flex-1 truncate rounded-sm bg-[hsl(230_20%_12%)] px-3 py-1 font-display text-[10px] font-light tracking-wide text-muted-foreground/85">
            {project.domain}
          </span>
          {upcoming ? (
            <Timer className="h-3 w-3 shrink-0 text-accent/80" />
          ) : (
            <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground/60" />
          )}
        </div>

        {/* Screenshot */}
        <div className="relative h-[210px] w-full overflow-hidden sm:h-[280px] lg:h-[340px]">
          <img
            src={project.image}
            alt={`Website ${project.title}`}
            loading="lazy"
            className="h-full w-full object-cover object-top transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,transparent_45%,hsl(0_0%_0%/0.85))]"
          />

          {upcoming && (
            <span className="absolute right-5 top-5 inline-flex items-center gap-2 border border-accent/50 bg-background/70 px-3 py-1.5 font-display text-[9px] font-light uppercase tracking-[0.24em] text-foreground/90 backdrop-blur-md sm:right-7 sm:top-7">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
              </span>
              bald live
            </span>
          )}

          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-5 p-5 sm:p-7">
            <div className="min-w-0">
              <p
                className={`font-display text-[10px] font-light uppercase tracking-[0.28em] ${
                  upcoming ? "text-accent" : "text-primary-glow"
                }`}
              >
                {project.branch}
                {project.location ? ` · ${project.location}` : ""}
              </p>
              <h3 className="mt-2 truncate font-display text-lg font-light tracking-wide text-foreground sm:text-xl">
                {project.title}
              </h3>
              <p className="mt-1 text-xs font-light text-muted-foreground">
                {project.result}
              </p>
            </div>

            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center border bg-background/60 text-muted-foreground backdrop-blur-md transition-[border-color,color,transform] duration-500 group-hover:-translate-y-0.5 ${
                upcoming
                  ? "border-border/70 group-hover:border-accent/60 group-hover:text-accent"
                  : "border-border/70 group-hover:border-primary/60 group-hover:text-primary-glow"
              }`}
            >
              {upcoming ? (
                <Timer className="h-4 w-4" strokeWidth={1.6} />
              ) : (
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.6} />
              )}
            </span>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

/**
 * Wiederverwendbare Projekt-Collage mit Swipe/Drag (Embla),
 * Coverflow-Effekt, Auto-Play, Pfeilen, Punkten und Zähler.
 */
export const ProjectCarousel = ({
  id,
  eyebrow,
  title,
  description,
  items,
  hint,
  footerText,
  footerLink,
  upcoming = false,
  className = "",
}) => {
  const [headRef, headInView] = useInView({ threshold: 0.18 });
  const [api, setApi] = React.useState(null);
  const [selected, setSelected] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    if (!api) return undefined;
    const onSelect = () => setSelected(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  React.useEffect(() => {
    if (!api || paused) return undefined;
    const timer = window.setInterval(() => api.scrollNext(), upcoming ? 6500 : 5500);
    return () => window.clearInterval(timer);
  }, [api, paused, upcoming]);

  const accent = upcoming ? "text-accent" : "text-primary-glow";

  return (
    <section
      id={id}
      className={`grain relative isolate overflow-hidden bg-[hsl(0_0%_0%)] py-24 sm:py-28 lg:py-32 ${className}`}
    >
      <StarField count={40} className="-z-20 opacity-45" />
      <div
        aria-hidden
        className="absolute left-1/2 top-[-10%] -z-20 h-[560px] w-[900px] -translate-x-1/2 rounded-full blur-3xl"
        style={{
          background: upcoming
            ? "radial-gradient(closest-side, hsl(265 90% 55% / 0.16), transparent)"
            : "radial-gradient(closest-side, hsl(230 90% 50% / 0.16), transparent)",
        }}
      />

      <div className="mx-auto max-w-[1500px] px-5 sm:px-8 lg:px-12">
        {/* Kopf */}
        <div
          ref={headRef}
          className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between"
        >
          <div
            className={`max-w-2xl transition-[opacity,transform] duration-700 ease-out ${
              headInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
          >
            <p
              className={`font-display text-[11px] font-light uppercase tracking-[0.32em] ${accent}`}
            >
              {eyebrow}
            </p>
            <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              {title}
            </h2>
            <div className="mt-7 h-px w-32 bg-gradient-hairline" aria-hidden />
          </div>

          <div
            className={`flex flex-col gap-6 transition-[opacity,transform] delay-150 duration-700 ease-out ${
              headInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
          >
            <p className="max-w-sm text-sm font-light leading-relaxed text-muted-foreground sm:text-base">
              {description}
            </p>

            <div className="flex items-center gap-5">
              <span className="font-display text-sm font-light tracking-[0.2em] text-foreground/85">
                {String(selected + 1).padStart(2, "0")}
                <span className="text-muted-foreground/60">
                  {" "}/ {String(items.length).padStart(2, "0")}
                </span>
              </span>

              <span className="h-px w-16 bg-border" aria-hidden />

              <div className="flex gap-2.5">
                <button
                  type="button"
                  aria-label="Vorheriges Projekt"
                  onClick={() => api && api.scrollPrev()}
                  className="flex h-11 w-11 items-center justify-center border border-border/70 text-muted-foreground outline-none transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-primary/60 hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" strokeWidth={1.6} />
                </button>
                <button
                  type="button"
                  aria-label="Nächstes Projekt"
                  onClick={() => api && api.scrollNext()}
                  className="flex h-11 w-11 items-center justify-center border border-border/70 text-muted-foreground outline-none transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-primary/60 hover:text-foreground"
                >
                  <ArrowRight className="h-4 w-4" strokeWidth={1.6} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Collage */}
        <div
          className="relative mt-14 lg:mt-16"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onPointerDown={() => setPaused(true)}
        >
          <Carousel
            setApi={setApi}
            opts={{ loop: true, align: "center" }}
            className="w-full"
          >
            <CarouselContent className="-ml-6 cursor-grab py-6 active:cursor-grabbing">
              {items.map((project, index) => (
                <CarouselItem
                  key={project.id}
                  className="basis-[88%] pl-6 sm:basis-[72%] lg:basis-[58%]"
                >
                  <Slide
                    project={project}
                    isActive={index === selected}
                    offset={index - selected}
                    upcoming={upcoming}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 hidden w-24 bg-[linear-gradient(90deg,hsl(0_0%_0%),transparent)] lg:block"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 hidden w-24 bg-[linear-gradient(270deg,hsl(0_0%_0%),transparent)] lg:block"
          />
        </div>

        {/* Punkte + Hinweis */}
        <div className="mt-8 flex flex-col items-center gap-6 sm:flex-row sm:justify-between">
          <div className="flex items-center gap-2">
            {items.map((project, index) => (
              <button
                key={project.id}
                type="button"
                aria-label={`${project.title} anzeigen`}
                onClick={() => api && api.scrollTo(index)}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
                  index === selected
                    ? `w-10 ${upcoming ? "bg-accent" : "bg-primary"}`
                    : "w-3 bg-border hover:bg-muted-foreground/60"
                }`}
              />
            ))}
          </div>

          <p className="flex items-center gap-2.5 text-xs font-light tracking-wide text-muted-foreground/80">
            <MoveHorizontal
              className={`h-4 w-4 ${accent}`}
              strokeWidth={1.5}
            />
            {hint || "Ziehen oder wischen zum Blättern"}
          </p>
        </div>

        {/* Abschluss */}
        {(footerText || footerLink) && (
          <div className="mt-16 flex flex-col items-start gap-6 border-t border-border/70 pt-10 sm:flex-row sm:items-center sm:justify-between">
            {footerText && (
              <p className="text-sm font-light leading-relaxed text-muted-foreground">
                {footerText}
              </p>
            )}
            {footerLink && (
              <a
                href={footerLink.href}
                className="group inline-flex items-center gap-2 font-display text-sm font-light tracking-wide text-foreground/85 transition-colors duration-300 hover:text-foreground"
              >
                {footerLink.label}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
